import React, { useState, useEffect, useMemo, useCallback } from 'react';

// スコア記号と数値の対応マップ
const scoreMap = {
  '♦': 5,
  '金': 4,
  '銀': 3,
  '銅': 2,
  '鉄': 1,
  '': 0
};

// localStorageのキー
const LOCAL_STORAGE_KEY = 'golfOlympicScoreData';

// メインアプリケーションコンポーネント
const App = () => {
  const initialPlayersCount = 4;
  const initialHoles = 18;

  // localStorageからデータをロードする関数
  const loadInitialData = () => {
    try {
      const data = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (data) {
        const parsedData = JSON.parse(data);
        const loadedPlayers = parsedData.players.slice(0, initialPlayersCount).map(p => ({
            ...p,
            scores: p.scores.map(s => ({ symbol: s.symbol || '', extraPoints: s.extraPoints || 0 }))
        }));
        
        const missingPlayersCount = initialPlayersCount - loadedPlayers.length;
        const newPlayers = Array.from({ length: missingPlayersCount }, (_, i) => ({
            id: loadedPlayers.length + i,
            name: '',
            scores: Array(initialHoles).fill({ symbol: '', extraPoints: 0 })
        }));

        return {
          players: [...loadedPlayers, ...newPlayers].map((p, index) => ({...p, id: index})), 
          startType: parsedData.startType || 'OUT',
        };
      }
    } catch (error) {
      console.error("Error loading data from localStorage:", error);
    }
    return {
      players: Array.from({ length: initialPlayersCount }, (_, i) => ({
        id: i,
        name: '',
        scores: Array(initialHoles).fill({ symbol: '', extraPoints: 0 })
      })),
      startType: 'OUT'
    };
  };

  const initialData = loadInitialData();
  
  const [players, setPlayers] = useState(initialData.players);
  const [isDataChanged, setIsDataChanged] = useState(false);
  const [startType, setStartType] = useState(initialData.startType);
  const [activeHoleIndex, setActiveHoleIndex] = useState(0);
  const [olympicResults, setOlympicResults] = useState([]);
  const [displayHoleScores, setDisplayHoleScores] = useState(() => {
    const initialDisplay = {};
    Array.from({ length: initialPlayersCount }, (_, i) => ({ id: i })).forEach(player => {
      initialDisplay[player.id] = Array(initialHoles).fill({ base: 0, extra: 0, fourOfAKindBonus: 0, setBonus: 0 });
    });
    return initialDisplay;
  });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [playerCurrentBonuses, setPlayerCurrentBonuses] = useState({}); // 消費後の残メダル数（リーチ判定用）
  const [playerTotalMedals, setPlayerTotalMedals] = useState({});       // 総獲得メダル数（表示用）
  const [playerReachStatus, setPlayerReachStatus] = useState({});
  // スコアテーブルの表示/非表示状態
  const [isTableVisible, setIsTableVisible] = useState(true);

  // トグル関数
  const toggleTableVisibility = () => setIsTableVisible(prev => !prev);
  // 有効なプレイヤーの数を計算
  const activePlayerCount = players.filter(p => p.name.trim() !== '').length;

  // ホールの表示順序を決定する関数
  const getHoleDisplayOrder = useCallback(() => {
    const holes = Array.from({ length: initialHoles }, (_, i) => i);
    if (startType === 'IN') {
      return [...holes.slice(9), ...holes.slice(0, 9)].map(i => i + 1);
    }
    if (startType === 'ETC') {
      return holes.map(i => {
        if (i < 9) {
          return `${i + 1}`;
        } else {
          return `${(i + 1) - 9}*`;
        }
      });
    }
    return holes.map(i => i + 1);
  }, [startType]);
  
  const holeDisplayOrder = useMemo(() => getHoleDisplayOrder(), [getHoleDisplayOrder]);

  // スタート方式変更時のエフェクト
  useEffect(() => {
    if (startType === 'IN') {
      setActiveHoleIndex(9);
    } else {
      setActiveHoleIndex(0);
    }
  }, [startType]);
  
  // データ永続化/データ変更フラグのエフェクト
  useEffect(() => {
    const dataToSave = {
        players: players.map(p => ({
            id: p.id,
            name: p.name,
            scores: p.scores.map(s => ({ symbol: s.symbol, extraPoints: s.extraPoints }))
        })),
        startType: startType,
    };
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(dataToSave));
    
    const hasDataChanged = players.some(player => 
        player.name.trim() !== '' || 
        player.scores.some(score => score.symbol !== '' || score.extraPoints !== 0)
    );
    setIsDataChanged(hasDataChanged);
    
  }, [players, startType]);


  // スコア計算とリーチ判定のエフェクト
  useEffect(() => {
    const activePlayers = players.filter(player => player.name.trim() !== '');

    if (activePlayers.length < 2) {
      setOlympicResults([]);
      setPlayerCurrentBonuses({});
      setPlayerTotalMedals({});
      setPlayerReachStatus({});
      const resetDisplayScores = {};
      players.forEach(player => {
        resetDisplayScores[player.id] = Array(initialHoles).fill({ base: 0, extra: 0, fourOfAKindBonus: 0, setBonus: 0 });
      });
      setDisplayHoleScores(resetDisplayScores);
      return;
    }

    const numberOfOtherPlayers = activePlayers.length - 1;

    const playerTotalScores = activePlayers.map(player => ({
      id: player.id,
      name: player.name,
      totalScore: 0
    }));

    const playerBonusStates = {};
    activePlayers.forEach(player => {
      playerBonusStates[player.id] = {
        symbolCounts: { '♦': 0, '金': 0, '銀': 0, '銅': 0, '鉄': 0 },
        straightSymbolsCollected: new Set(),
      };
    });

    const tempDisplayHoleScores = {};
    players.forEach(player => {
      tempDisplayHoleScores[player.id] = Array(initialHoles).fill({ base: 0, extra: 0, fourOfAKindBonus: 0, setBonus: 0 });
    });

    for (let i = 0; i < initialHoles; i++) {
      activePlayers.forEach(player => {
        const scoreData = player.scores[i];
        const currentSymbol = scoreData.symbol;
        const currentExtraPoints = scoreData.extraPoints || 0;

        let currentHolePoints = (scoreMap[currentSymbol] || 0) + currentExtraPoints;
        let fourOfAKindBonusApplied = 0;
        let setBonusApplied = 0;

        const playerState = playerBonusStates[player.id];
        
        // --- カード追加 (ボーナス計算用) ---
        playerState.symbolCounts[currentSymbol]++;
        if (['金', '銀', '銅', '鉄'].includes(currentSymbol)) {
          playerState.straightSymbolsCollected.add(currentSymbol);
        }

        let bestPotentialBonus = 0;
        let chosenBonusType = '';
        let daConsumedForChosenBonus = 0;
        let chosenFourOfAKindSymbol = '';

        // 1. 役満 (フォーカード) 判定
        for (const sym of ['金', '銀', '銅', '鉄']) {
          const currentMedalCount = playerState.symbolCounts[sym];
          const currentDaCount = playerState.symbolCounts['♦'];

          if (currentMedalCount >= 4) {
            const bonus = scoreMap[sym] * 4;
            if (bonus > bestPotentialBonus) {
              bestPotentialBonus = bonus;
              chosenBonusType = 'fourOfAKind';
              chosenFourOfAKindSymbol = sym;
              daConsumedForChosenBonus = 0;
            }
          } else if (currentMedalCount === 3 && currentDaCount >= 1) {
            const bonus = scoreMap[sym] * 4;
            if (bonus > bestPotentialBonus) {
              bestPotentialBonus = bonus;
              chosenBonusType = 'fourOfAKind';
              chosenFourOfAKindSymbol = sym;
              daConsumedForChosenBonus = 1;
            }
          }
        }

        // 2. セット (ストレート) 判定
        const currentStraightSize = playerState.straightSymbolsCollected.size;
        const currentDaCountForStraight = playerState.symbolCounts['♦'];

        if (currentStraightSize === 4) {
          const bonus = 10;
          if (bonus >= bestPotentialBonus) { 
            bestPotentialBonus = bonus;
            chosenBonusType = 'straight';
            daConsumedForChosenBonus = 0;
          }
        } else if (currentStraightSize === 3 && currentDaCountForStraight >= 1) {
          const bonus = 10;
          if (bonus >= bestPotentialBonus) {
            bestPotentialBonus = bonus;
            chosenBonusType = 'straight';
            daConsumedForChosenBonus = 1;
          }
        }

        // --- ボーナス適用とカードの消費 ---
        if (chosenBonusType !== '') {
          currentHolePoints += bestPotentialBonus;

          if (chosenBonusType === 'fourOfAKind') {
            fourOfAKindBonusApplied = bestPotentialBonus;
            
            playerState.symbolCounts[chosenFourOfAKindSymbol] = 
                playerState.symbolCounts[chosenFourOfAKindSymbol] - 4;
            
            if (daConsumedForChosenBonus > 0) {
              playerState.symbolCounts['♦'] -= daConsumedForChosenBonus;
            }
            
            if (playerState.straightSymbolsCollected.has(chosenFourOfAKindSymbol)) {
                playerState.straightSymbolsCollected.delete(chosenFourOfAKindSymbol);
            }
            
          } else if (chosenBonusType === 'straight') {
            setBonusApplied = bestPotentialBonus;
            
            playerState.straightSymbolsCollected.forEach(sym => {
                playerState.symbolCounts[sym] = 0;
            });
            
            playerState.straightSymbolsCollected.clear();
            
            if (daConsumedForChosenBonus > 0) {
              playerState.symbolCounts['♦'] -= daConsumedForChosenBonus;
            }
          }
        }

        const targetPlayer = playerTotalScores.find(p => p.id === player.id);
        if (targetPlayer) {
          targetPlayer.totalScore += currentHolePoints;
        }

        tempDisplayHoleScores[player.id][i] = {
          base: scoreMap[currentSymbol] || 0,
          extra: currentExtraPoints,
          fourOfAKindBonus: fourOfAKindBonusApplied,
          setBonus: setBonusApplied
        };
        
        playerBonusStates[player.id].totalScore = targetPlayer.totalScore;
      });
    }

    // オリンピック最終結果の計算
    setOlympicResults(prevResults => {
      const individualOlympicResults = playerTotalScores.map(currentPlayer => {
        const ownTotalScore = currentPlayer.totalScore;
        let sumOfOtherPlayersTotalScores = 0;
        playerTotalScores.forEach(otherPlayer => {
          if (otherPlayer.id !== currentPlayer.id) {
            sumOfOtherPlayersTotalScores += otherPlayer.totalScore;
          }
        });
        const calculatedScore = (ownTotalScore * numberOfOtherPlayers) - sumOfOtherPlayersTotalScores;
        return { name: currentPlayer.name, score: calculatedScore, totalPointsEarned: ownTotalScore };
      });

      if (JSON.stringify(prevResults) !== JSON.stringify(individualOlympicResults)) {
        return individualOlympicResults;
      }
      return prevResults;
    });

    setDisplayHoleScores(tempDisplayHoleScores);
    
    // --- 獲得メダル数とリーチ状態の更新 ---
    const currentBonusesMap = {};
    const totalMedalsMap = {}; 
    const reachStatusMap = {};
    
    activePlayers.forEach(player => {
        const state = playerBonusStates[player.id];
        currentBonusesMap[player.id] = state;
        
        // --- 獲得メダル数の集計 (累積枚数) ---
        totalMedalsMap[player.id] = {
            symbolCounts: {
                '♦': player.scores.reduce((sum, score) => sum + (score.symbol === '♦' ? 1 : 0), 0),
                '金': player.scores.reduce((sum, score) => sum + (score.symbol === '金' ? 1 : 0), 0),
                '銀': player.scores.reduce((sum, score) => sum + (score.symbol === '銀' ? 1 : 0), 0),
                '銅': player.scores.reduce((sum, score) => sum + (score.symbol === '銅' ? 1 : 0), 0),
                '鉄': player.scores.reduce((sum, score) => sum + (score.symbol === '鉄' ? 1 : 0), 0),
            },
            totalScore: state.totalScore
        };

        // --- リーチ状態判定ロジック (消費後の状態を使用) ---
        const reaches = { 
            fourOfAKind: new Set(), 
            straight: false         
        };
        const currentDaCount = state.symbolCounts['♦'];
        const currentStraightSize = state.straightSymbolsCollected.size;

        // 1. フォーカードリーチ判定 (金・銀・銅・鉄)
        for (const sym of ['金', '銀', '銅', '鉄']) {
            const count = state.symbolCounts[sym];
            if (count === 3) {
                reaches.fourOfAKind.add(sym);
            } else if (count === 2 && currentDaCount >= 1) {
                reaches.fourOfAKind.add(sym);
            }
        }
        
        // 2. ストレートリーチ判定 (金・銀・銅・鉄のうち3種類 + ♦で達成可能)
        if (currentStraightSize === 3) {
            reaches.straight = true;
        } else if (currentStraightSize === 2 && currentDaCount >= 1) { 
             reaches.straight = true;
        }

        reachStatusMap[player.id] = reaches;
    });

    setPlayerCurrentBonuses(currentBonusesMap);
    setPlayerTotalMedals(totalMedalsMap); // 総獲得メダル数を更新
    setPlayerReachStatus(reachStatusMap);
    
  }, [players, startType, initialHoles]);


  // ブラウザリロード時の確認ダイアログ
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isDataChanged) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDataChanged]);


  // プレイヤー名を更新する関数（連続入力・NULL禁止ロジック）
  const handlePlayerNameChange = (playerId, newName) => {
    setPlayers(prevPlayers => {
        const oldPlayer = prevPlayers.find(p => p.id === playerId);
        
        // 1. 名前を空欄にする操作の場合の処理（NULL禁止）
        if (newName.trim() === '') {
            if (oldPlayer && oldPlayer.name.trim() !== '') {
                return prevPlayers; 
            }
        }

        // 2. 名前が入力された場合（通常更新）
        return prevPlayers.map(player =>
            player.id === playerId ? { ...player, name: newName } : player
        );
    });
  };

  // スコア・追加点数変更関数
  const handleScoreChange = (playerId, holeIndex, newScoreSymbol) => {
    setPlayers(prevPlayers => {
      if (['金', '銀', '銅', '鉄'].includes(newScoreSymbol)) {
        const isMedalTaken = prevPlayers.some(p => 
          p.id !== playerId && p.scores[holeIndex].symbol === newScoreSymbol
        );
        if (isMedalTaken) {
          return prevPlayers;
        }
      }
      return prevPlayers.map(player =>
        player.id === playerId
          ? {
              ...player,
              scores: player.scores.map((score, idx) =>
                idx === holeIndex ? { ...score, symbol: newScoreSymbol } : score
              )
            }
          : player
      )
    });
  };

  const handleExtraPointsChange = (playerId, holeIndex, value) => {
    const points = Math.max(0, parseInt(value, 10) || 0);
    setPlayers(prevPlayers =>
      prevPlayers.map(player =>
        player.id === playerId
          ? {
              ...player,
              scores: player.scores.map((score, idx) =>
                idx === holeIndex ? { ...score, extraPoints: points } : score
              )
            }
          : player
      )
    );
  };

  // リセット関数
  const resetApp = () => {
    setShowConfirmDialog(true);
  };

  const handleResetConfirm = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    
    setPlayers(
      Array.from({ length: initialPlayersCount }, (_, i) => ({
        id: i,
        name: '',
        scores: Array(initialHoles).fill({ symbol: '', extraPoints: 0 })
      }))
    );
    setStartType('OUT');
    setActiveHoleIndex(0);
    setOlympicResults([]);
    setPlayerCurrentBonuses({});
    setPlayerTotalMedals({});
    setPlayerReachStatus({});
    const resetDisplayScores = {};
    Array.from({ length: initialPlayersCount }, (_, i) => ({ id: i })).forEach(player => {
      resetDisplayScores[player.id] = Array(initialHoles).fill({ base: 0, extra: 0, fourOfAKindBonus: 0, setBonus: 0 });
    });
    setDisplayHoleScores(resetDisplayScores);
    setShowConfirmDialog(false);
  };

  const handleResetCancel = () => {
    setShowConfirmDialog(false);
  };

  // メダル使用状況関数
  const getUsedMedalsForHole = (currentHoleIndex, currentPlayerId) => {
    const usedMedals = new Set();
    players.forEach(player => {
      if (player.id !== currentPlayerId) {
        const symbol = player.scores[currentHoleIndex].symbol;
        if (['金', '銀', '銅', '鉄'].includes(symbol)) {
          usedMedals.add(symbol);
        }
      }
    });
    return usedMedals;
  };
  
  // プレイヤーの並び替え関数（UIからボタンを削除済み）
  const handlePlayerReorder = (playerId, direction) => {
      setPlayers(prevPlayers => {
          const index = prevPlayers.findIndex(p => p.id === playerId);
          if (index === -1 || prevPlayers[index].name.trim() === '') return prevPlayers;

          const activeCount = prevPlayers.filter(p => p.name.trim() !== '').length;

          let newPlayers = [...prevPlayers];
          let newIndex = index;

          if (direction === 'up') {
              newIndex = Math.max(0, index - 1);
          } else if (direction === 'down') {
              newIndex = Math.min(activeCount - 1, index + 1);
          }

          if (newIndex === index) return prevPlayers;

          const [movedPlayer] = newPlayers.splice(index, 1);
          newPlayers.splice(newIndex, 0, movedPlayer);
          
          return newPlayers.map((p, i) => ({ ...p, id: i }));
      });
  };

  /**
   * メダル表示の文字列を決定するヘルパー関数
   */
  const getMedalDisplay = (medalSymbol, count) => {
    if (medalSymbol === '♦') {
        if (count === 0) return '♦0';
        return `${count}`; 
    }
    
    if (count === 0) {
      return `${medalSymbol}0`; 
    } else {
      return `${count}`; 
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 p-2 font-sans md:p-4">
      <div className="max-w-7xl mx-auto bg-white rounded-xl shadow-2xl p-4 md:p-8">
        {/* タイトル */}
        <h1 className="text-2xl md:text-4xl font-extrabold text-center text-green-800 mb-6 md:mb-8 tracking-tight">
          ⛳ ゴルフ オリンピック計算 ⛳
        </h1>

        {/* プレイヤー名入力 & 中間スコアセクション (常に表示) */}
        <div className="mb-6 md:mb-8 p-3 bg-green-50 rounded-lg shadow-inner">
          <h2 className="text-xl md:text-2xl font-semibold text-green-700 mb-3">中間スコア</h2> 
          <div className="space-y-1"> 
            {players.map((player, index) => {
                const bonusState = playerCurrentBonuses[player.id];
                const totalMedals = playerTotalMedals[player.id]; 
                
                const reachState = playerReachStatus[player.id] || { fourOfAKind: new Set(), straight: false };
                const totalScore = bonusState ? bonusState.totalScore : 0;
                
                const dCount = totalMedals ? totalMedals.symbolCounts['♦'] : 0;
                const gCount = totalMedals ? totalMedals.symbolCounts['金'] : 0;
                const sCount = totalMedals ? totalMedals.symbolCounts['銀'] : 0;
                const cCount = totalMedals ? totalMedals.symbolCounts['銅'] : 0;
                const tCount = totalMedals ? totalMedals.symbolCounts['鉄'] : 0;
                
                const isPlayerActive = player.name.trim() !== '';

                const isEditable = isPlayerActive || index === 0 || players[index - 1]?.name.trim() !== '';
                if (!isEditable && !isPlayerActive) return null;
                
                const getMedalClass = (medal) => {
                    const isFourOfAKindReach = reachState.fourOfAKind.has(medal);
                    const isStraightReachAndOwned = reachState.straight && bonusState?.symbolCounts[medal] > 0;
                    
                    if (isFourOfAKindReach || isStraightReachAndOwned) {
                        return 'animate-pulse ring-2 ring-red-500 rounded-full px-1'; 
                    }
                    return '';
                };

                const isDiamondAssistingReach = (bonusState ? bonusState.symbolCounts['♦'] : 0) > 0 && 
                    (reachState.straight || Array.from(reachState.fourOfAKind).length > 0);


                return (
                  <div key={player.id} className={`flex items-center gap-1 p-2 rounded-lg ${isPlayerActive ? 'bg-white shadow' : 'bg-gray-100'}`}> 
                    
                    {/* プレイヤー名入力 */}
                    <input
                      type="text"
                      value={player.name}
                      onChange={e => handlePlayerNameChange(player.id, e.target.value)}
                      className={`flex-shrink-0 w-1/4 p-1 border rounded-lg focus:ring-2 focus:border-transparent transition duration-200 ease-in-out text-sm md:text-base text-gray-700 placeholder-gray-400 font-semibold
                        ${isEditable ? 'border-green-300 focus:ring-green-400' : 'border-gray-300 bg-gray-200 cursor-not-allowed'}`}
                      placeholder={`P${index + 1}`}
                      disabled={!isEditable}
                      title={!isEditable ? '前のプレイヤーの名前を入力してください' : ''}
                    />
                    
                    {/* 中間スコア */}
                    <div className="flex-grow text-center min-w-[50px] md:min-w-[60px] flex-shrink-0"> 
                        <span className="text-2xl md:text-3xl font-extrabold text-green-600">{totalScore}</span>
                    </div>

                    {/* 所持カードカウンター */}
                    <div className="flex-shrink-0 flex items-center gap-1 text-sm md:text-lg font-semibold text-gray-700"> 
                        {/* ダイヤ */}
                        <span className={`text-xl md:text-2xl text-red-500 ${isDiamondAssistingReach ? 'animate-pulse ring-2 ring-blue-500 rounded-full px-1' : ''}`} title="♦ (ダイヤ)">
                            {getMedalDisplay('♦', dCount)} 
                        </span>
                        {/* 金 */}
                        <span className={`text-xl md:text-2xl text-yellow-500 ${getMedalClass('金')}`} title="金メダル">
                            {getMedalDisplay('金', gCount)}
                        </span>
                        {/* 銀 */}
                        <span className={`text-xl md:text-2xl text-gray-400 ${getMedalClass('銀')}`} title="銀メダル">
                            {getMedalDisplay('銀', sCount)}
                        </span>
                        {/* 銅 */}
                        <span className={`text-xl md:text-2xl text-amber-700 ${getMedalClass('銅')}`} title="銅メダル">
                            {getMedalDisplay('銅', cCount)}
                        </span>
                        {/* 鉄 */}
                        <span className={`text-xl md:text-2xl text-gray-500 ${getMedalClass('鉄')}`} title="鉄メダル">
                            {getMedalDisplay('鉄', tCount)}
                        </span>
                    </div>
                  </div>
                );
            })}
          </div>
        </div>
        
        {/* スタート方式選択ラジオボタン */}
        <div className="flex flex-wrap justify-center gap-4 mb-6 md:mb-8 p-3 bg-blue-50 rounded-lg shadow-inner">
          <label className="inline-flex items-center text-sm md:text-lg font-medium text-blue-800 cursor-pointer">
            <input
              type="radio"
              className="form-radio h-4 w-4 text-blue-600 border-blue-300 focus:ring-blue-500"
              name="startType"
              value="OUT"
              checked={startType === 'OUT'}
              onChange={() => setStartType('OUT')}
            />
            <span className="ml-1">OUTスタート</span>
          </label>
          <label className="inline-flex items-center text-sm md:text-lg font-medium text-blue-800 cursor-pointer">
            <input
              type="radio"
              className="form-radio h-4 w-4 text-blue-600 border-blue-300 focus:ring-blue-500"
              name="startType"
              value="IN"
              checked={startType === 'IN'}
              onChange={() => setStartType('IN')}
            />
            <span className="ml-1">INスタート</span>
          </label>
          <label className="inline-flex items-center text-sm md:text-lg font-medium text-blue-800 cursor-pointer">
            <input
              type="radio"
              className="form-radio h-4 w-4 text-blue-600 border-blue-300 focus:ring-blue-500"
              name="startType"
              value="ETC"
              checked={startType === 'ETC'}
              onChange={() => setStartType('ETC')}
            />
            <span className="ml-1">ETCスタート</span>
          </label>
        </div>
        
        {/* --- */}

        {/* スコア入力テーブル (表示切替の対象) */}
        <div className={`overflow-x-auto mb-6 md:mb-8 rounded-lg shadow-lg transition-all duration-300 ${isTableVisible ? 'block' : 'hidden'}`}> 
          <table className="w-full border-collapse">
            <thead className="bg-green-600 text-white text-sm md:text-base">
              <tr>
                {/* プレイヤー名の列のヘッダー */}
                <th className="p-2 md:p-3 text-left sticky left-0 bg-green-600 z-20 w-[60px] md:w-[80px]">Player</th> 
                {holeDisplayOrder.map((holeNumber, index) => {
                  const actualHoleIndex = startType === 'IN' ? (index < 9 ? index + 9 : index - 9) : index;
                  return (
                    <th 
                      key={index} 
                      className={`p-2 md:p-3 text-center min-w-[100px] cursor-pointer transition duration-200 ease-in-out transform 
                                ${activeHoleIndex === actualHoleIndex 
                                  ? 'bg-blue-600 scale-100 shadow-md'
                                  : 'bg-green-600 hover:bg-green-700'
                                }`}
                      onClick={() => setActiveHoleIndex(actualHoleIndex)}
                    >
                      H{holeNumber}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {players.map((player, playerIndex) => {
                const usedMedals = getUsedMedalsForHole(activeHoleIndex, player.id);
                const isPlayerActive = player.name.trim() !== '';

                if (!isPlayerActive) return null;

                return (
                  <tr key={player.id} className="bg-white border-b border-green-100 last:border-b-0 hover:bg-green-50 transition duration-150 ease-in-out text-sm md:text-base">
                    {/* プレイヤー名（固定幅を最小限に） */}
                    <td className="p-2 font-medium text-gray-800 sticky left-0 bg-white hover:bg-green-50 z-10 w-[60px] md:w-[80px] overflow-hidden text-ellipsis whitespace-nowrap" title={player.name}>
                        {player.name || `P${playerIndex + 1}`}
                    </td>
                    {holeDisplayOrder.map((_, index) => {
                      const holeIndex = startType === 'IN' ? (index < 9 ? index + 9 : index - 9) : index;
                      const isInputActive = activeHoleIndex === holeIndex && isPlayerActive;

                      return (
                        <td key={index} className="p-1 md:p-2 text-center">
                          {isInputActive ? (
                            <>
                              <select
                                value={player.scores[holeIndex].symbol}
                                onChange={e => handleScoreChange(player.id, holeIndex, e.target.value)}
                                className="w-24 p-1 border border-gray-300 rounded-md text-center focus:ring-1 focus:ring-blue-400 focus:border-transparent transition duration-200 ease-in-out text-gray-700 bg-white mx-auto block font-bold"
                              >
                                <option value=""></option>
                                <option value="♦">♦ (5)</option>
                                {['金', '銀', '銅', '鉄'].map(medal => (
                                  <option 
                                    key={medal}
                                    value={medal} 
                                    disabled={usedMedals.has(medal)}
                                  >
                                    {medal} ({scoreMap[medal]})
                                  </option>
                                ))}
                              </select>
                              <input
                                type="number"
                                min="0"
                                value={player.scores[holeIndex].extraPoints || ''}
                                onChange={e => handleExtraPointsChange(player.id, holeIndex, e.target.value)}
                                className="w-24 p-1 mt-1 border border-gray-300 rounded-md text-center focus:ring-1 focus:ring-blue-400 focus:border-transparent transition duration-200 ease-in-out text-gray-700 bg-white mx-auto block"
                                placeholder="加点"
                              />
                            </>
                          ) : (
                            <div className="flex flex-col items-center justify-center p-2 min-h-[60px] md:min-h-[80px]">
                              <span className="text-xl font-bold text-gray-800">
                                {player.scores[holeIndex].symbol}
                              </span>
                              {player.scores[holeIndex].extraPoints > 0 && (
                                <span className="text-sm font-semibold text-gray-600 mt-1">
                                  +{player.scores[holeIndex].extraPoints}
                                </span>
                              )}
                            </div>
                          )}
                          {player.scores[holeIndex].symbol && displayHoleScores[player.id] && displayHoleScores[player.id][holeIndex] && (
                            <p className="text-xs text-gray-500 mt-1">
                                <span className='font-bold'>
                                    { (scoreMap[player.scores[holeIndex].symbol] || 0) + player.scores[holeIndex].extraPoints + displayHoleScores[player.id][holeIndex].fourOfAKindBonus + displayHoleScores[player.id][holeIndex].setBonus }点
                                </span>
                                <br />
                                (基:{displayHoleScores[player.id][holeIndex].base}
                                {displayHoleScores[player.id][holeIndex].extra > 0 ? `+加:${displayHoleScores[player.id][holeIndex].extra}` : ''}
                                {displayHoleScores[player.id][holeIndex].fourOfAKindBonus !== 0 ? `+役:${displayHoleScores[player.id][holeIndex].fourOfAKindBonus}` : ''}
                                {displayHoleScores[player.id][holeIndex].setBonus !== 0 ? `+組:${displayHoleScores[player.id][holeIndex].setBonus}` : ''})
                            </p>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* --- */}

        {/* オリンピック計算結果表示 */}
        <div className="p-4 bg-yellow-50 rounded-lg shadow-xl text-center mb-6">
          <h2 className="text-2xl md:text-3xl font-bold text-yellow-800 mb-4">計算結果 (オリンピック点)</h2>
          {olympicResults.length > 0 ? (
            <div className="space-y-2">
              {olympicResults.sort((a, b) => b.score - a.score).map((result, index) => {
                const playerName = players.find(p => p.name === result.name)?.name || result.name || `P${index + 1}`;
                return (
                    <p key={index} className="text-xl md:text-2xl font-semibold text-yellow-900">
                      {playerName} (累計:{result.totalPointsEarned}点): 
                      <span className="text-3xl md:text-4xl font-extrabold ml-2">{result.score}</span>点
                    </p>
                );
              })}
            </div>
          ) : (
            <p className="text-lg text-gray-600">
              計算には2人以上のプレイヤー名が必要です。
            </p>
          )}
        </div>

        {/* 最下部コントロールエリア (トグルボタンとリセットボタン) */}
        <div className="mt-6 flex justify-center items-center gap-4">
          
          {/* 👈 NEW: スコア入力テーブル表示切替ボタン (リセットボタンの左) */}
          <button 
              onClick={toggleTableVisibility}
              className="flex-shrink-0 text-white font-bold text-xl bg-blue-600 hover:bg-blue-700 rounded-full w-10 h-10 flex items-center justify-center shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 ease-in-out transform hover:scale-110"
              title={isTableVisible ? 'スコア入力を非表示' : 'スコア入力を表示'}
          >
              {isTableVisible ? '▲' : '▼'}
          </button>
          
          <button
            onClick={resetApp}
            className="flex items-center px-6 py-3 bg-gray-700 text-white text-base md:text-lg font-bold rounded-full shadow-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-75 transition duration-300 ease-in-out transform hover:scale-105"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004 12c0 2.21-.817 4.227 2.158 5.828M20 20v-5h-.581m0 0a8.001 8.001 0 01-15.357-2A8.001 8.001 0 0120 12c0-2.21-.817-4.227-2.158-5.828"></path></svg>
            全データリセット
          </button>
        </div>

        {showConfirmDialog && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl text-center max-w-xs w-full">
              <p className="text-xl font-semibold text-gray-800 mb-4">本当に全てのデータをリセットしますか？</p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={handleResetConfirm}
                  className="px-4 py-2 bg-red-500 text-white text-sm font-semibold rounded-lg shadow-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 transition duration-300 ease-in-out"
                >
                  はい、削除します
                </button>
                <button
                  onClick={handleResetCancel}
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-sm font-semibold rounded-lg shadow-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-300 ease-in-out"
                >
                  いいえ、キャンセル
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;